/**
 * Privacy Service
 * 
 * Service for managing user privacy settings
 * Following the service pattern from preference-service.ts
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import type {
  PrivacySettingsResponse,
  UpdatePrivacySettingsInput,
  SuccessResponse,
} from '../types/security.types';

// Helper function to convert database row to PrivacySettingsResponse data
function convertDbRowToPrivacySettings(row: any): PrivacySettingsResponse['data'] {
  return {
    privacyModeEnabled: row.privacy_mode_enabled || false,
    dataAnalyticsEnabled: row.data_analytics_enabled || false,
    marketingCommunicationsEnabled: row.marketing_communications_enabled || false,
  };
}

// Helper function to convert UpdatePrivacySettingsInput to database format
function convertUpdateInputToDb(input: UpdatePrivacySettingsInput): any {
  const dbInput: any = {};
  
  if (input.privacyModeEnabled !== undefined) {
    dbInput.privacy_mode_enabled = input.privacyModeEnabled;
  }
  if (input.dataAnalyticsEnabled !== undefined) {
    dbInput.data_analytics_enabled = input.dataAnalyticsEnabled;
  }
  if (input.marketingCommunicationsEnabled !== undefined) {
    dbInput.marketing_communications_enabled = input.marketingCommunicationsEnabled;
  }
  
  dbInput.updated_at = new Date().toISOString();
  
  return dbInput;
}

export const privacyService = {
  /**
   * Get current user's privacy settings
   * Creates default settings if none exist (all false - opt-in model)
   */
  async getPrivacySettings(userId?: string): Promise<PrivacySettingsResponse> {
    try {
      const currentUserId = userId || await getCurrentUserId();
      
      if (!currentUserId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Get privacy settings from user_privacy_settings table
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_privacy_settings')
        .select('*')
        .eq('user_id', currentUserId)
        .single();

      if (settingsError) {
        if (settingsError.code === 'PGRST116') {
          // Settings not found - create default settings (all false - opt-in model)
          const insertData = {
            user_id: currentUserId,
            privacy_mode_enabled: false,
            data_analytics_enabled: false,
            marketing_communications_enabled: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          const { data: newSettings, error: insertError } = await supabase
            .from('user_privacy_settings')
            .insert(insertData)
            .select()
            .single();

          if (insertError) {
            return {
              error: {
                code: 'INSERT_ERROR',
                message: insertError.message || 'Failed to create default privacy settings'
              }
            };
          }

          return {
            data: convertDbRowToPrivacySettings(newSettings)
          };
        }
        
        return {
          error: {
            code: 'NOT_FOUND',
            message: settingsError.message || 'Privacy settings not found'
          }
        };
      }

      const settings = convertDbRowToPrivacySettings(settingsData);

      return {
        data: settings
      };
    } catch (error: any) {
      console.error('Error getting privacy settings:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to get privacy settings'
        }
      };
    }
  },

  /**
   * Update privacy settings
   */
  async updatePrivacySettings(userId: string, input: UpdatePrivacySettingsInput): Promise<SuccessResponse> {
    try {
      const currentUserId = userId || await getCurrentUserId();
      
      if (!currentUserId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      const dbInput = convertUpdateInputToDb(input);

      const { error: updateError } = await supabase
        .from('user_privacy_settings')
        .update(dbInput)
        .eq('user_id', currentUserId);

      if (updateError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: updateError.message || 'Failed to update privacy settings'
          }
        };
      }

      return {
        data: {
          success: true
        }
      };
    } catch (error: any) {
      console.error('Error updating privacy settings:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to update privacy settings'
        }
      };
    }
  },
};

